import {
  conditionGroupFromJson,
  conditionGroupToJson,
  conditionSentence,
  rawFieldLabel,
  type ConditionNode,
} from '@/helpers/conditionTree.helpers';
import { formatAnalysisPathLabel } from '@/helpers/analysisRelationshipPath.helpers';
import type {
  AnalysisRelationshipHop,
  AnalysisRelationshipPath,
} from '@/helpers/analysisRelationshipPath.helpers';
import type {
  AnalysisJsonObject,
  AnalysisJsonValue,
  AnalysisRecipeStep,
} from '@/types/analysisRecipe.types';
import { SEARCH_RELEVANCE_COLUMNS } from '@/constants/pipelines.constants';

export type AnalysisRollupMetric =
  | 'count'
  | 'count_distinct'
  | 'sum'
  | 'avg'
  | 'min'
  | 'max';

export type AnalysisCardinalityHint = 'one' | 'many' | 'unknown';

/**
 * Which of several matches a lookup takes, when the path reaches more than one.
 *
 * `direction` is the backend op, not a sort order: ordering is expressed by
 * choosing the extreme you want — `first` takes the minimum of `field`, `last`
 * the maximum. There is no separate asc/desc, and adding one would invent a
 * combination the engine has no shape for.
 *
 * `type` tells the engine how to compare. It is derived from the field's schema
 * kind rather than chosen by the user, because getting it wrong is not a
 * preference but an error: `'numeric'` over a date raises on Memgraph.
 *
 * Absent entirely means the legacy behaviour — `first` with no ordering rule,
 * which returns an arbitrary match. Kept as the default so saved steps compile
 * to exactly the query they did before ordering existed.
 */
export interface AnalysisLookupOrderBy {
  field: string;
  direction: 'first' | 'last';
  type?: 'datetime' | 'numeric';
}

export interface NormalizedAnalysisLookupOutput {
  field: string;
  outputColumn: string | null;
  hopIndex: number | null;
  condition: AnalysisJsonObject | null;
  /** Value to read when the looked-up cell is blank; `null` when unset. */
  ifBlank: string | number | null;
}

export interface NormalizedAnalysisLookupConfig {
  path: AnalysisRelationshipPath;
  targetFields: string[];
  intermediateOutputs: NormalizedAnalysisLookupOutput[];
  configuredOutputs: NormalizedAnalysisLookupOutput[] | null;
  condition: AnalysisJsonObject | null;
}

export interface NormalizedAnalysisRollupMetric {
  metric: AnalysisRollupMetric;
  field?: string;
  outputName?: string;
  outputColumn: string | null;
  condition: AnalysisJsonObject | null;
}

export interface BuildLookupStepInput {
  id?: string;
  sourceLabel: string;
  path: AnalysisRelationshipPath;
  targetFields: string[];
  cardinalityHint?: AnalysisCardinalityHint;
  /**
   * What `path_fanout` actually measured for this path, when it was measured.
   *
   * Stored beside `cardinalityHint` rather than replacing it: the hint is the
   * user's own statement and a measurement must not silently overwrite it. The
   * warning logic prefers a user override, then this, then 'unknown'.
   */
  measuredCardinality?: 'one_to_one' | 'zero_or_one' | 'one_to_many';
  /** When the measurement was taken, so a stale one can be recognised. */
  measuredAt?: string;
  /** Which match to take. Absent = the legacy arbitrary `first`. */
  orderBy?: AnalysisLookupOrderBy;
  condition?: AnalysisJsonObject | null;
  /**
   * Blank fallback per target field. Per field rather than per step because one
   * lookup carries fields of mixed kinds — a number is illegal on a text column
   * and `Unknown` is illegal on a numeric one — and each already has its own
   * entry in `outputs`. Fields absent from the map keep no fallback.
   */
  fallbacks?: Record<string, string | number>;
}

export interface BuildRollupStepInput {
  id?: string;
  sourceLabel: string;
  path: AnalysisRelationshipPath;
  metric?: AnalysisRollupMetric;
  field?: string;
  metrics?: Array<{
    metric: AnalysisRollupMetric;
    field?: string;
    outputName?: string;
    outputColumn?: string;
    condition?: AnalysisJsonObject | null;
  }>;
  /**
   * Restricts which related rows the metric sees, over the terminal record's
   * own fields. Compiled into the aggregation's `where`, so an unconditional
   * rollup and a conditional one differ only by this group.
   */
  condition?: AnalysisJsonObject | null;
}

export type AnalysisCalcOperator =
  | 'add'
  | 'subtract'
  | 'multiply'
  | 'divide'
  | 'concat'
  | 'pctOf'
  | 'coalesce';

export interface AnalysisCalcOperand {
  kind: 'column' | 'literal';
  value: string | number;
  /** Human-readable label when `value` is a generated internal field key. */
  label?: string;
  /**
   * Value to read when this operand's cell is blank. Without it a single
   * missing lookup blanks the whole formula, which is silent and unexplained;
   * with it the row keeps a value the author chose.
   */
  ifBlank?: string | number;
}

export const normalizeAnalysisNumericLiteral = (
  value: unknown,
): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== 'string' || value.trim() === '') return null;
  const numeric = Number(value.trim());
  return Number.isFinite(numeric) ? numeric : null;
};

interface BuildFormulaCalcStepInput {
  /**
   * Formula sentences name their operands from `operands[].label`, not from a
   * condition, so there is nothing here for a resolver to do. `never` keeps the
   * union's branches exclusive the way `flagCondition?: never` does below, and
   * the branch labels are flag-only for the same reason.
   */
  labelFor?: never;
  trueLabel?: never;
  falseLabel?: never;
  id?: string;
  sourceLabel: string;
  outputColumn: string;
  operator: AnalysisCalcOperator;
  operands?: AnalysisCalcOperand[];
  /** Legacy binary authoring input retained for callers migrating to operands. */
  left?: AnalysisCalcOperand;
  right?: AnalysisCalcOperand;
  flagCondition?: never;
}

interface BuildFlagCalcStepInput {
  /**
   * Resolves a sheet column to its display label for the persisted sentence.
   * Omitted only where no map exists; the editor always supplies one.
   */
  labelFor?: (field: string) => string;
  id?: string;
  sourceLabel: string;
  outputColumn: string;
  flagCondition: AnalysisJsonObject;
  /** Emitted when the condition matches. Blank or absent means `Yes`. */
  trueLabel?: string;
  /** Emitted when it does not. Blank or absent means `No`. */
  falseLabel?: string;
  operator?: never;
  operands?: never;
  left?: never;
  right?: never;
}

export type BuildCalcStepInput =
  | BuildFormulaCalcStepInput
  | BuildFlagCalcStepInput;

const configObject = (value: AnalysisJsonValue | undefined): AnalysisJsonObject | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : null;

const configString = (value: AnalysisJsonValue | undefined): string | null =>
  typeof value === 'string' ? value : null;

/**
 * The ordering rule a saved lookup step carries, or `null` for the legacy
 * arbitrary pick. Validated rather than cast: a stored `direction` outside
 * first/last, or a missing field, would compile to a pipeline the backend
 * rejects, and the honest fallback is the behaviour the step had before.
 */
export function analysisLookupOrderBy(
  step: AnalysisRecipeStep,
): AnalysisLookupOrderBy | null {
  const stored = configObject(step.config.orderBy);
  const field = configString(stored?.field);
  const direction = configString(stored?.direction);
  if (!field || (direction !== 'first' && direction !== 'last')) return null;
  const type = configString(stored?.type);
  return {
    field,
    direction,
    ...(type === 'datetime' || type === 'numeric' ? { type } : {}),
  };
}

export function normalizeAnalysisLookupConfig(
  step: AnalysisRecipeStep,
): NormalizedAnalysisLookupConfig {
  const pathConfig = configObject(step.config.path);
  const path: AnalysisRelationshipPath = {
    hops: Array.isArray(pathConfig?.hops)
      ? (pathConfig.hops as unknown as AnalysisRelationshipHop[])
      : [],
  };
  const condition = configObject(step.config.condition);
  const targetFields = Array.isArray(step.config.targetFields)
    ? step.config.targetFields.filter(
        (field): field is string => typeof field === 'string',
      )
    : [];
  const intermediateOutputs = Array.isArray(step.config.intermediateFields)
    ? step.config.intermediateFields.flatMap((value) => {
        const output = configObject(value);
        const field = configString(output?.field);
        if (!field) return [];
        return [{
          field,
          outputColumn: configString(output?.outputColumn),
          hopIndex:
            typeof output?.hopIndex === 'number' ? output.hopIndex : null,
          condition: configObject(output?.condition),
          // An intermediate identity column names the record passed through; a
          // fallback there would invent a hop that was never traversed.
          ifBlank: null,
        }];
      })
    : [];
  const configuredOutputs = Array.isArray(step.config.outputs)
    ? step.config.outputs.flatMap((value, index) => {
        const output = configObject(value);
        const field = configString(output?.field);
        if (!field) return [];
        return [{
          field,
          outputColumn:
            configString(output?.outputColumn) ??
            step.outputColumns[index] ??
            null,
          hopIndex:
            typeof output?.hopIndex === 'number' ? output.hopIndex : null,
          condition: configObject(output?.condition ?? step.config.condition),
          ifBlank:
            typeof output?.ifBlank === 'string' ||
            typeof output?.ifBlank === 'number'
              ? output.ifBlank
              : null,
        }];
      })
    : null;

  return {
    path,
    targetFields,
    intermediateOutputs,
    configuredOutputs,
    condition,
  };
}

export function normalizeAnalysisRollupMetrics(
  step: AnalysisRecipeStep,
  options: { fallbackOnEmpty?: boolean } = {},
): NormalizedAnalysisRollupMetric[] {
  const condition = configObject(step.config.condition);
  if (
    Array.isArray(step.config.metrics) &&
    (!options.fallbackOnEmpty || step.config.metrics.length > 0)
  ) {
    return step.config.metrics.flatMap((value, index) => {
      const metric = configObject(value);
      if (!metric) return [];
      const outputColumn =
        configString(metric.outputColumn) ?? step.outputColumns[index] ?? null;
      if (!outputColumn) return [];
      return [{
        metric: String(metric.metric ?? 'count') as AnalysisRollupMetric,
        ...(configString(metric.field)
          ? { field: configString(metric.field)! }
          : {}),
        ...(configString(metric.outputName)
          ? { outputName: configString(metric.outputName)! }
          : {}),
        outputColumn,
        condition: configObject(metric.condition ?? step.config.condition),
      }];
    });
  }
  const outputColumn = step.outputColumns[0] ?? null;
  if (!outputColumn) return [];
  return [{
    metric: String(step.config.metric ?? 'count') as AnalysisRollupMetric,
    ...(configString(step.config.field)
      ? { field: configString(step.config.field)! }
      : {}),
    outputColumn,
    condition,
  }];
}

/** The two strings a flag column emits, one per branch. */
export interface AnalysisFlagLabels {
  true: string;
  false: string;
}

/** What a flag emitted before the labels were authorable, and still the default. */
export const ANALYSIS_FLAG_DEFAULT_LABELS: AnalysisFlagLabels = {
  true: 'Yes',
  false: 'No',
};

/**
 * The labels a flag step emits, read from its config in ONE place.
 *
 * Three surfaces have to agree on these: the `case` expression the backend
 * runs, the browser-side preview, and the persisted sentence. Reading them
 * separately is how they would drift, so every caller comes here.
 *
 * A flag authored before labels existed carries neither key, and a blank label
 * is not a label — both fall back to Yes/No rather than emitting an empty
 * string the reader cannot interpret.
 */
export const analysisFlagLabels = (
  config: AnalysisJsonObject,
): AnalysisFlagLabels => ({
  true:
    configString(config.flagTrueLabel)?.trim() ||
    ANALYSIS_FLAG_DEFAULT_LABELS.true,
  false:
    configString(config.flagFalseLabel)?.trim() ||
    ANALYSIS_FLAG_DEFAULT_LABELS.false,
});

const stepId = (): string =>
  `step-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export const analysisMetricLabel = (
  metric: AnalysisRollupMetric,
  field?: string,
): string => {
  switch (metric) {
    case 'count':
      return 'Count';
    case 'count_distinct':
      return 'Count distinct';
    case 'sum':
      return field ? `Sum of ${field}` : 'Sum';
    case 'avg':
      return field ? `Average of ${field}` : 'Average';
    case 'min':
      return field ? `Minimum of ${field}` : 'Minimum';
    case 'max':
      return field ? `Maximum of ${field}` : 'Maximum';
    default:
      return metric;
  }
};

const encodeAnalysisFieldToken = (value: string): string =>
  Array.from(value)
    .map((character) =>
      character.codePointAt(0)!.toString(16).padStart(6, '0'),
    )
    .join('');

export function analysisStepOutputField(
  stepIdValue: string,
  columnKey: string,
): string {
  return `analysis__${encodeAnalysisFieldToken(
    stepIdValue,
  )}__${encodeAnalysisFieldToken(columnKey)}`;
}

/**
 * Columns the `search` op adds, which look like plain properties but are not.
 *
 * Without this they are mirrored into graph filters and emitted as an
 * `order_by` to the entity endpoint against columns no node has.
 */
const SEARCH_RELEVANCE_FIELDS: ReadonlySet<string> = new Set(
  SEARCH_RELEVANCE_COLUMNS,
);

export const isAnalysisGeneratedField = (field: string): boolean =>
  /^analysis__[a-f0-9]+__[a-f0-9]+$/.test(field) ||
  /^analysis:[^:]+:.+$/.test(field) ||
  SEARCH_RELEVANCE_FIELDS.has(field);

export function buildLookupAnalysisStep(
  input: BuildLookupStepInput,
): AnalysisRecipeStep {
  const id = input.id ?? stepId();
  const terminalLabel =
    input.path.hops[input.path.hops.length - 1]?.targetLabel ?? '';
  const outputColumns = input.targetFields.map((field) =>
    analysisStepOutputField(id, field),
  );
  const cardinalityHint = input.cardinalityHint ?? 'unknown';
  /**
   * Three-way, because the honest statement differs. An ordered pick names its
   * rule, so there is nothing arbitrary left to warn about. An unordered pick
   * over a to-many path still takes whichever match the engine reaches first.
   * A to-one path has one match and needs no note at all.
   */
  const arbitraryNote = input.orderBy
    ? `, ${input.orderBy.direction === 'first' ? 'earliest' : 'latest'} by ${input.orderBy.field}`
    : cardinalityHint === 'many' || cardinalityHint === 'unknown'
      ? ' (one match, no order — may change between runs)'
      : '';
  const fieldList =
    input.targetFields.length > 0 ? input.targetFields.join(', ') : 'fields';
  const conditionGroup = conditionGroupFromJson(input.condition ?? null);
  const conditionClause = conditionGroup.conditions.length
    // Over the related record's own properties, which are already the names the
    // reader sees — there is no display map to resolve them through.
    ? ` where ${conditionSentence(conditionGroup, rawFieldLabel)}`
    : '';
  const nodeChain = [
    input.sourceLabel,
    ...input.path.hops.map((hop) => hop.targetLabel),
  ].join(' → ');
  const fallbackClause = (() => {
    const stated = input.targetFields
      .filter((field) => input.fallbacks?.[field] !== undefined)
      .map((field) => {
        const value = input.fallbacks?.[field];
        return `${typeof value === 'string' ? `“${value}”` : value} when ${field} is blank`;
      });
    return stated.length > 0 ? `, using ${stated.join(' and ')}` : '';
  })();
  const sentence = `Lookup ${fieldList} via ${nodeChain}${conditionClause}${fallbackClause}${arbitraryNote}`;

  const config: AnalysisJsonObject = {
    path: input.path as unknown as AnalysisJsonObject,
    targetLabel: terminalLabel,
    targetFields: input.targetFields,
    cardinalityHint,
    ...(input.measuredCardinality
      ? { measuredCardinality: input.measuredCardinality }
      : {}),
    ...(input.measuredAt ? { measuredAt: input.measuredAt } : {}),
    ...(input.orderBy
      ? { orderBy: input.orderBy as unknown as AnalysisJsonObject }
      : {}),
    ...(conditionGroup.conditions.length
      ? { condition: conditionGroupToJson(conditionGroup) }
      : {}),
    outputs: input.targetFields.map((field, index) => ({
      field,
      outputColumn: outputColumns[index],
      hopIndex: Math.max(0, input.path.hops.length - 1),
      ...(conditionGroup.conditions.length
        ? { condition: conditionGroupToJson(conditionGroup) }
        : {}),
      ...(input.fallbacks?.[field] !== undefined
        ? { ifBlank: input.fallbacks[field] }
        : {}),
    })),
  };

  return {
    id,
    kind: 'lookup',
    sourceLabel: input.sourceLabel,
    outputColumns,
    sentence,
    config,
  };
}

export function buildRollupAnalysisStep(
  input: BuildRollupStepInput,
): AnalysisRecipeStep {
  const id = input.id ?? stepId();
  const terminalLabel =
    input.path.hops[input.path.hops.length - 1]?.targetLabel ?? '';
  const metrics =
    input.metrics && input.metrics.length > 0
      ? input.metrics
      : [
          {
            metric: input.metric ?? 'count',
            field: input.field,
            condition: input.condition,
          },
        ];
  // Two metrics can share metric and field while differing only by their
  // per-metric `condition` — the documented conditional/unconditional pairing.
  // That is invisible to the key, so both would resolve to one output field and
  // one value would overwrite the other. Suffix only on an actual collision, so
  // the ordinary single-metric step keeps the key it has always had.
  const usedColumnKeys = new Set<string>();
  const normalizedMetrics = metrics.map((metric) => {
    const baseKey =
      metric.outputName ??
      (metric.metric === 'count' || metric.metric === 'count_distinct'
        ? metric.metric
        : `${metric.metric}_${metric.field ?? 'value'}`);
    let columnKey = baseKey;
    for (let suffix = 2; usedColumnKeys.has(columnKey); suffix += 1) {
      columnKey = `${baseKey}_${suffix}`;
    }
    usedColumnKeys.add(columnKey);
    return {
      ...metric,
      outputName:
        metric.outputName ??
        analysisMetricLabel(metric.metric, metric.field),
      outputColumn:
        metric.outputColumn ?? analysisStepOutputField(id, columnKey),
    };
  });
  const outputColumns = normalizedMetrics.map((metric) => metric.outputColumn);
  const pathLabel = formatAnalysisPathLabel(input.sourceLabel, input.path);
  const conditionGroup = conditionGroupFromJson(input.condition ?? null);
  const conditionClause = conditionGroup.conditions.length
    // Over the related record's own properties, which are already the names the
    // reader sees — there is no display map to resolve them through.
    ? ` where ${conditionSentence(conditionGroup, rawFieldLabel)}`
    : '';
  const metricSentence = normalizedMetrics
    .map(
      (metric) =>
        `${metric.outputName} (${analysisMetricLabel(metric.metric, metric.field)})`,
    )
    .join(', ');
  const sentence = `${metricSentence} via ${pathLabel}${conditionClause}`;

  const config: AnalysisJsonObject = {
    path: input.path as unknown as AnalysisJsonObject,
    targetLabel: terminalLabel,
    metric: normalizedMetrics[0].metric,
    field: normalizedMetrics[0].field ?? null,
    metrics: normalizedMetrics.map((metric) => ({
      metric: metric.metric,
      field: metric.field ?? null,
      outputName: metric.outputName,
      outputColumn: metric.outputColumn,
      ...(metric.condition ? { condition: metric.condition } : {}),
    })),
    ...(conditionGroup.conditions.length
      ? { condition: conditionGroupToJson(conditionGroup) }
      : {}),
  };

  return {
    id,
    kind: 'rollup',
    sourceLabel: input.sourceLabel,
    outputColumns,
    sentence,
    config,
  };
}

const calcOperatorLabel: Record<AnalysisCalcOperator, string> = {
  add: 'plus',
  subtract: 'minus',
  multiply: 'times',
  divide: 'divided by',
  concat: '&',
  pctOf: 'as a percent of',
  coalesce: 'otherwise',
};

export interface BuildJoinStepInput {
  id?: string;
  sourceLabel: string;
  /** Property on the sheet's own records that carries the matching value. */
  sourceField: string;
  targetLabel: string;
  /** Property on the joined label that must equal it. */
  targetField: string;
  /** Fields to bring back from the matched record. */
  targetFields: string[];
}

/**
 * A related-data step that matches on a VALUE instead of a relationship.
 *
 * `Stage.jobnumber = Job.node_id` is a real join in the data that the schema
 * has no edge for, so no traversal can reach it. It compiles to the backend's
 * `join_on_value`, which pairs two labels on one equality — see
 * `compileAnalysisRecipe` for why that forces its own chain rather than
 * extending the relationship one.
 */
export function buildJoinAnalysisStep(
  input: BuildJoinStepInput,
): AnalysisRecipeStep {
  const id = input.id ?? stepId();
  const outputColumns = input.targetFields.map((field) =>
    analysisStepOutputField(id, `${field} (${input.targetLabel})`),
  );
  const fieldList =
    input.targetFields.length > 0 ? input.targetFields.join(', ') : 'fields';
  return {
    id,
    kind: 'join',
    sourceLabel: input.sourceLabel,
    outputColumns,
    // "matching records only" is load-bearing, not a flourish: `join_on_value`
    // is an INNER join — its Cypher matches the right node with a required
    // MATCH and the op takes no outer/optional argument — so a source record
    // with no match leaves the sheet. A sentence reading like a plain lookup
    // made that look like data loss; this is a DURABLE string, so it is the
    // chip and the column tooltip too.
    sentence: `Add ${fieldList} where ${input.sourceLabel}.${input.sourceField} = ${input.targetLabel}.${input.targetField} (matching records only)`,
    config: {
      sourceField: input.sourceField,
      targetLabel: input.targetLabel,
      targetField: input.targetField,
      targetFields: input.targetFields,
      outputs: input.targetFields.map((field, index) => ({
        field,
        outputColumn: outputColumns[index],
      })),
    },
  };
}

/**
 * The join a sheet is sourced through, or `null`.
 *
 * At most one: the join replaces `rows_from_nodes` as the row source, so two
 * would be two different sources for one sheet. The authoring UI offers one,
 * and the compiler reads the first if a hand-edited recipe carries more.
 */
export const analysisJoinStep = (
  steps: AnalysisRecipeStep[],
): AnalysisRecipeStep | null =>
  steps.find((step) => step.kind === 'join') ?? null;

export interface AnalysisJoinConfig {
  sourceField: string;
  targetLabel: string;
  targetField: string;
  outputs: Array<{ field: string; outputColumn: string }>;
}

export const normalizeAnalysisJoinConfig = (
  step: AnalysisRecipeStep,
): AnalysisJoinConfig | null => {
  const sourceField = configString(step.config.sourceField);
  const targetLabel = configString(step.config.targetLabel);
  const targetField = configString(step.config.targetField);
  if (!sourceField || !targetLabel || !targetField) return null;
  const outputs = Array.isArray(step.config.outputs)
    ? step.config.outputs.flatMap((value) => {
        const output = configObject(value);
        const field = configString(output?.field);
        const outputColumn = configString(output?.outputColumn);
        if (!field || !outputColumn) return [];
        return [{ field, outputColumn }];
      })
    : [];
  if (outputs.length === 0) return null;
  return { sourceField, targetLabel, targetField, outputs };
};

export function buildCalcAnalysisStep(
  input: BuildCalcStepInput,
): AnalysisRecipeStep {
  const id = input.id ?? stepId();
  const outputColumn = input.outputColumn.trim() || 'Calculated value';
  const flagCondition = input.flagCondition;
  if (flagCondition) {
    const condition = conditionGroupFromJson(flagCondition);
    const labels = analysisFlagLabels({
      ...(input.trueLabel ? { flagTrueLabel: input.trueLabel } : {}),
      ...(input.falseLabel ? { flagFalseLabel: input.falseLabel } : {}),
    });
    return {
      id,
      kind: 'calc',
      sourceLabel: input.sourceLabel,
      outputColumns: [analysisStepOutputField(id, outputColumn)],
      // This sentence is DURABLE — it is the recipe-bar chip and the column
      // tooltip, not a transient string. A flag reads the sheet's own columns,
      // so without the caller's map it bakes an encoded key into saved state.
      sentence: `${outputColumn} = ${labels.true} when ${conditionSentence(condition, input.labelFor ?? rawFieldLabel)}, otherwise ${labels.false}`,
      config: {
        outputColumn,
        flagCondition: conditionGroupToJson(condition),
        // Stored only when they differ from Yes/No, so a plain flag's config
        // stays exactly what it has always been and `analysisFlagLabels`
        // supplies the default for both the old shape and the new.
        ...(labels.true !== ANALYSIS_FLAG_DEFAULT_LABELS.true
          ? { flagTrueLabel: labels.true }
          : {}),
        ...(labels.false !== ANALYSIS_FLAG_DEFAULT_LABELS.false
          ? { flagFalseLabel: labels.false }
          : {}),
      },
    };
  }
  const operands =
    input.operands && input.operands.length > 0
      ? input.operands
      : [input.left, input.right].filter(
          (operand): operand is AnalysisCalcOperand => Boolean(operand),
        );
  const operator = input.operator ?? 'add';
  const normalizedOperands =
    operator === 'concat' || operator === 'coalesce'
      ? operands
      : operands.map((operand) => {
          if (operand.kind !== 'literal') return operand;
          const value = normalizeAnalysisNumericLiteral(operand.value);
          if (value === null) {
            throw new Error('Arithmetic literals require a finite numeric value');
          }
          return { ...operand, value };
        });
  const operandSentence = normalizedOperands
    .map((operand) => {
      const name = (operand.label ?? operand.value) || '…';
      return operand.ifBlank === undefined
        ? name
        : `${name} (blank → ${operand.ifBlank})`;
    })
    .join(` ${calcOperatorLabel[operator]} `);
  return {
    id,
    kind: 'calc',
    sourceLabel: input.sourceLabel,
    outputColumns: [analysisStepOutputField(id, outputColumn)],
    sentence: `${outputColumn} = ${operandSentence}`,
    config: {
      outputColumn,
      operator,
      operands: normalizedOperands as unknown as AnalysisJsonObject[],
    },
  };
}

/** A cell the browser cannot know: it only exists in a backend result. */
const UNAVAILABLE = Symbol('analysis-unavailable');

const readCalcOperand = (
  operand: AnalysisJsonValue | undefined,
  row: Record<string, unknown>,
): unknown | typeof UNAVAILABLE => {
  if (
    !operand ||
    typeof operand !== 'object' ||
    Array.isArray(operand)
  ) {
    return '';
  }
  const kind = operand.kind;
  const value = operand.value;
  const ifBlank =
    typeof operand.ifBlank === 'string' || typeof operand.ifBlank === 'number'
      ? operand.ifBlank
      : undefined;
  if (kind !== 'column') {
    return typeof value === 'string' || typeof value === 'number' ? value : '';
  }
  if (typeof value !== 'string') return '';
  const cell = row[value];
  // Unknown is not blank, and the unavailable check must come first. A
  // calculation over a column only the backend can fill (a lookup/rollup output,
  // or an unfetched property) has no browser-side answer — and
  // `applyAnalysisPreviewColumns` deliberately nulls exactly those columns to say
  // so. Letting a chosen blank fallback answer here would fabricate a number for
  // a value nobody has computed, making the preview disagree with the eventual
  // executed result.
  if (cell === undefined || (cell === null && isAnalysisGeneratedField(value))) {
    return UNAVAILABLE;
  }
  // A genuinely absent value is what the author chose a fallback for.
  if (ifBlank !== undefined && (cell === null || cell === '')) return ifBlank;
  return cell;
};

const calculateValue = (
  step: AnalysisRecipeStep,
  row: Record<string, unknown>,
): unknown => {
  const flagCondition =
    step.config.flagCondition &&
    typeof step.config.flagCondition === 'object' &&
    !Array.isArray(step.config.flagCondition)
      ? conditionGroupFromJson(step.config.flagCondition)
      : null;
  if (flagCondition) {
    // Same rule as `readCalcOperand`: a condition the browser cannot decide has
    // no preview answer, so it must propagate as unavailable rather than resolve
    // to a definitive 'No'. An existence condition needs the graph, and a
    // backend-filled column is null here only because nobody has computed it.
    const matchesCondition = (
      condition: ConditionNode,
    ): boolean | typeof UNAVAILABLE => {
      if (condition.type === 'related') return UNAVAILABLE;
      if (condition.type === 'group') {
        const matches = condition.conditions.map(matchesCondition);
        if (matches.some((match) => match === UNAVAILABLE)) return UNAVAILABLE;
        return condition.joiner === 'all'
          ? matches.every(Boolean)
          : matches.some(Boolean);
      }
      const leaf = condition;
      const actual = row[leaf.field];
      if (
        actual === undefined ||
        (actual === null && isAnalysisGeneratedField(leaf.field))
      ) {
        return UNAVAILABLE;
      }
      const expected = leaf.value;
      switch (leaf.operator) {
        case 'is':
          return String(actual ?? '') === expected;
        case 'isNot':
          return String(actual ?? '') !== expected;
        case 'gt':
          return Number(actual) > Number(expected);
        case 'gte':
          return Number(actual) >= Number(expected);
        case 'lt':
          return Number(actual) < Number(expected);
        case 'lte':
          return Number(actual) <= Number(expected);
        case 'contains':
          return String(actual ?? '').includes(expected);
        case 'notContains':
          return !String(actual ?? '').includes(expected);
        case 'startsWith':
          return String(actual ?? '').startsWith(expected);
        case 'notStartsWith':
          return !String(actual ?? '').startsWith(expected);
        case 'endsWith':
          return String(actual ?? '').endsWith(expected);
        case 'isOneOf':
          return expected
            .split(',')
            .map((value) => value.trim())
            .includes(String(actual ?? ''));
        case 'absGt':
          return Math.abs(Number(actual)) > Number(expected);
        case 'isBlank':
          return actual === null || actual === undefined || actual === '';
        case 'notBlank':
          return actual !== null && actual !== undefined && actual !== '';
        default:
          return false;
      }
    };
    const matched = matchesCondition(flagCondition);
    if (matched === UNAVAILABLE) return null;
    // `UNAVAILABLE` is still null above, deliberately: a row the browser
    // cannot decide has no flag value, and emitting the false label would
    // assert the condition failed when nobody evaluated it.
    const labels = analysisFlagLabels(step.config);
    return matched ? labels.true : labels.false;
  }
  const configuredOperands = Array.isArray(step.config.operands)
    ? step.config.operands
    : [step.config.left, step.config.right];
  const operands = configuredOperands.map((operand) =>
    readCalcOperand(operand, row),
  );
  if (operands.some((operand) => operand === UNAVAILABLE)) return null;
  if (step.config.operator === 'coalesce') {
    return operands.find(
      (operand) => operand !== null && operand !== undefined && operand !== '',
    ) ?? null;
  }
  if (step.config.operator === 'concat') {
    return operands.map((operand) => operand ?? '').join('');
  }
  const normalizedNumbers = operands.map(normalizeAnalysisNumericLiteral);
  if (normalizedNumbers.some((value) => value === null)) return null;
  const numbers = normalizedNumbers as number[];
  const [leftNumber = 0, rightNumber = 0] = numbers;
  switch (step.config.operator) {
    case 'add':
      return numbers.reduce((sum, value) => sum + value, 0);
    case 'subtract':
      return leftNumber - rightNumber;
    case 'multiply':
      return leftNumber * rightNumber;
    case 'divide':
      return rightNumber === 0 ? null : leftNumber / rightNumber;
    case 'pctOf':
      return rightNumber === 0 ? null : (leftNumber / rightNumber) * 100;
    default:
      return null;
  }
};

/**
 * Adds a sheet's Made columns as empty cells. Used while an executable sheet is
 * still running or after it failed: the columns exist so the grid keeps its
 * shape, but no value is shown for a result that has not arrived.
 */
export function clearAnalysisComputedColumns<
  T extends Record<string, unknown>,
>(rows: T[], steps: AnalysisRecipeStep[]): T[] {
  if (steps.length === 0) return rows;
  const columns = steps.flatMap((step) => step.outputColumns);
  if (columns.length === 0) return rows;
  return rows.map((row) => {
    const next = { ...row } as Record<string, unknown>;
    columns.forEach((field) => {
      next[field] = null;
    });
    return next as T;
  });
}

/**
 * Fills the Made columns a browser can compute for itself, and leaves the rest
 * empty. Lookup and rollup values are resolved by traversing the graph, so they
 * are never invented here; calculations are evaluated only over operands the
 * row already carries. Reserved for recipes the backend cannot run at all — an
 * executable sheet shows its executed rows. Text-join used to be the standing
 * example and no longer is: `concat` executes, so a joined column now arrives
 * from the backend and this preview never sees it.
 */
export function applyAnalysisPreviewColumns<
  T extends Record<string, unknown>,
>(rows: T[], steps: AnalysisRecipeStep[]): T[] {
  if (steps.length === 0) return rows;
  // Match compiler phase order: lookups/rollups first, then calcs that may
  // depend on those Made columns.
  const backendOnlySteps = steps.filter((step) => step.kind !== 'calc');
  const calcSteps = steps.filter((step) => step.kind === 'calc');
  return rows.map((row) => {
    const next = { ...row } as Record<string, unknown>;
    backendOnlySteps.forEach((step) => {
      step.outputColumns.forEach((field) => {
        next[field] = null;
      });
    });
    calcSteps.forEach((step) => {
      step.outputColumns.forEach((field) => {
        next[field] = calculateValue(step, next);
      });
    });
    return next as T;
  });
}
